/**
 * Bridges Clerk org *membership* and the session's *active* organization.
 *
 * The whole app scopes requests off the active-org JWT claims (see
 * {@link useActiveOrg}). But a user who joins an org they didn't create through
 * the in-app <CreateOrganization> flow — an invited teammate, or an org
 * provisioned in the Clerk dashboard — ends up a *member* with no *active* org,
 * which strands them on the create-organization screen even though they already
 * belong somewhere.
 *
 * This auto-activates the sole membership (the common case) and exposes the full
 * list so multi-org users can pick one. Activation only happens automatically
 * once per signed-in user/membership while mounted; a failed attempt falls back
 * to the manual picker rather than retrying in a loop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';

export interface OrgMembershipOption {
  id: string;
  name: string;
}

export interface OrgActivation {
  /** Both Clerk resources have resolved. */
  isLoaded: boolean;
  /** An organization is active on this session. */
  hasActiveOrg: boolean;
  /** Orgs the user belongs to (may not be the active one). */
  memberships: OrgMembershipOption[];
  /** A setActive call is in flight. */
  activating: boolean;
  /** Membership-load or activation failure suitable for an inline retry state. */
  error: string | null;
  /** Activate a specific membership on this session. */
  activate: (organizationId: string) => Promise<boolean>;
  /** Retry loading Clerk memberships after a transient failure. */
  retry: () => void;
}

interface UseOrgActivationOptions {
  /** Disable in views that provide their own explicit picker. */
  autoActivate?: boolean;
}

export function useOrgActivation(
  { autoActivate = true }: UseOrgActivationOptions = {},
): OrgActivation {
  const { user } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { isLoaded: listLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 100 },
  });
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const autoAttemptedFor = useRef<string | null>(null);

  const memberships: OrgMembershipOption[] = (userMemberships?.data ?? []).map((m) => ({
    id: m.organization.id,
    name: m.organization.name,
  }));
  const membershipsLoading = userMemberships?.isLoading ?? false;
  const membershipsError = userMemberships?.isError
    ? 'We could not load your organization memberships. Please try again.'
    : null;
  const isLoaded = orgLoaded && listLoaded && !membershipsLoading;
  const hasActiveOrg = !!organization;

  const activate = useCallback(
    async (organizationId: string): Promise<boolean> => {
      if (!setActive) return false;
      setActivationError(null);
      setActivating(true);
      try {
        await setActive({ organization: organizationId });
        return true;
      } catch {
        setActivationError('We could not activate that organization. Please try again.');
        return false;
      } finally {
        setActivating(false);
      }
    },
    [setActive],
  );

  const retry = useCallback(() => {
    setActivationError(null);
    void userMemberships?.revalidate?.();
  }, [userMemberships]);

  // Auto-activate when the user belongs to exactly one org but none is active.
  useEffect(() => {
    if (!autoActivate || !user || !isLoaded || membershipsError || hasActiveOrg || activating) return;
    const onlyMembership = memberships.length === 1 && userMemberships?.count === 1
      ? memberships[0]
      : null;
    if (onlyMembership) {
      const attemptKey = `${user.id}:${onlyMembership.id}`;
      if (autoAttemptedFor.current === attemptKey) return;
      autoAttemptedFor.current = attemptKey;
      void activate(onlyMembership.id);
    }
    // memberships is a fresh array each render; guarded by the ref + flags above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoActivate,
    user?.id,
    isLoaded,
    membershipsError,
    hasActiveOrg,
    activating,
    memberships.length,
    userMemberships?.count,
    activate,
  ]);

  return {
    isLoaded,
    hasActiveOrg,
    memberships,
    activating,
    error: activationError ?? membershipsError,
    activate,
    retry,
  };
}
